import React, { useEffect, useRef, useState } from 'react';
import { 
  RefreshCw, ExternalLink, BarChart2, Inbox, 
  History, Clock, CheckCircle, ArrowRight, Type, Plus, Minus, Maximize2 
} from 'lucide-react';
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import api from './axiosConfig';
import { useFontSize } from './FontSizeContext';

// --- مكون الـ Workflow Inbox الجديد ---
const WorkflowInbox = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [activeTasks, setActiveTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('active'); 
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchNotifications = async () => {
      try {
          setLoading(true);
          const res = await api.get(`/notifications/all?skip=${skip}&limit=${limit}`);
          const allData = res.data.data;
          setTotal(res.data.total);
          setActiveTasks(allData.filter(n => n.is_active));
          setHistoryTasks(allData.filter(n => !n.is_active));
      } catch (err) {
          console.error("Error fetching notifications", err);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => { fetchNotifications(); }, [skip]);

  const PaginationControls = () => (
      <div className="flex justify-center items-center gap-4 mt-4 py-2 border-t border-line-subtle bg-surface-panel/50">
          <button
              disabled={skip === 0}
              onClick={() => setSkip(prev => Math.max(0, prev - limit))}
              className="p-1 px-3 bg-surface-card border border-line rounded text-[10px] font-bold disabled:opacity-30 hover:bg-surface-panel"
          >
              {t('dashboard.previous')}
          </button>
          <span className="text-[10px] font-black text-primary">
              {t('dashboard.showing')} {skip + 1} - {Math.min(skip + limit, total)} {t('dashboard.of')} {total}
          </span>
          <button
              disabled={skip + limit >= total}
              onClick={() => setSkip(prev => prev + limit)}
              className="p-1 px-3 bg-surface-card border border-line rounded text-[10px] font-bold disabled:opacity-30 hover:bg-surface-panel"
          >
              {t('dashboard.next')}
          </button>
      </div>
  );

  const handleAction = (link) => {
    window.location.href = link;
  };

  return (
    <div className={`bg-surface-card border border-line rounded-card shadow-card mt-6 overflow-hidden min-h-[500px] ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <div className="bg-primary-dark text-white px-3 py-1 flex justify-between items-center text-[11px] font-bold">
        <div className={`flex items-center gap-2 `}>
          <Inbox size={14} />
          <span>{t('dashboard.workflow_center')}</span>
        </div>
        <RefreshCw size={12} className="cursor-pointer" onClick={fetchNotifications} />
      </div>

      <div className="flex border-b border-line bg-surface-panel">
        <button
          onClick={() => setSubTab('active')}
          className={`px-6 py-2 text-[11px] font-black uppercase transition-all ${subTab === 'active' ? 'bg-surface-card border-r border-line text-primary shadow-sm' : 'text-ink-secondary hover:text-ink'}`}
        >
          {t('dashboard.active_tasks')} ({activeTasks.length})
        </button>
        <button
          onClick={() => setSubTab('history')}
          className={`px-6 py-2 text-[11px] font-black uppercase transition-all ${subTab === 'history' ? 'bg-surface-card border-l border-line text-primary shadow-sm' : 'text-ink-secondary hover:text-ink'}`}
        >
          {t('dashboard.history_tasks')} ({historyTasks.length})
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center p-10"><RefreshCw className="animate-spin text-primary" /></div>
        ) : (
          <div className="divide-y divide-line-subtle">
            {(subTab === 'active' ? activeTasks : historyTasks).length === 0 ? (
              <div className="text-center py-10 opacity-30 flex flex-col items-center">
                <Inbox size={40} />
                <span className="text-[12px] font-bold uppercase mt-2">{t('dashboard.no_data')}</span>
              </div>
            ) : (
              (subTab === 'active' ? activeTasks : historyTasks).map((task, idx) => (
                <div key={idx} className={`py-3 flex justify-between items-center group hover:bg-primary-soft px-2 rounded-md transition-all `}>
                  <div className={`flex gap-3 items-start `}>
                    {subTab === 'active' ? <Clock size={16} className="text-status-warning mt-1" /> : <CheckCircle size={16} className="text-status-success mt-1" />}
                    <div className="flex flex-col">
                      <span className="text-[12px] font-black text-ink">{task.title}</span>
                      <p className="text-[11px] text-ink-secondary leading-tight">{task.message}</p>
                      <div className={`flex gap-2 mt-1 items-center `}>
                         <span className="bg-primary-soft text-primary text-[9px] px-2 py-0.5 rounded font-bold">{task.app_name}</span>
                         <span className="text-[9px] text-ink-secondary font-bold">{new Date(task.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  {subTab === 'active' && (
                    <button
                      onClick={() => handleAction(task.link)}
                      className={`flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded text-[10px] font-bold hover:bg-primary-hover transition-all opacity-0 group-hover:opacity-100 shadow-md `}
                    >
                      {t('dashboard.take_action')} <ArrowRight size={12} className={i18n.language === 'ar' ? 'rotate-180' : ''} />
                    </button>
                  )}
                </div>
              ))
            )}
            <PaginationControls />
          </div>
        )}
      </div>
    </div>
  );
};

// --- المكونات الحالية (BI) ---
const MaxeamBI = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const dashboardRef = useRef(null);
  const containerRef = useRef(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const handleRefresh = () => { setRefreshKey(prev => prev + 1); };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        alert(`Error: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (dashboardRef.current) {
      dashboardRef.current.innerHTML = "";
      const SUPERSET_DOMAIN = window.RUNTIME_ENV.SUPERSET_DOMAIN || "https://superset.maqaree.com";
      const DASHBOARD_ID = window.RUNTIME_ENV.SUPERSET_DASHBOARD_ID || "54904224-ddc7-4414-8ab1-ed910a27445e";
      
      embedDashboard({
        id: DASHBOARD_ID, 
        supersetDomain: SUPERSET_DOMAIN,
        mountPoint: dashboardRef.current,
        fetchGuestToken: async () => {
          const response = await fetch("/api/bi/guest-token");
          const data = await response.json();
          return data.token;
        },
        dashboardUiConfig: {
          hideTitle: false,
          colorScheme: 'light',
          hideTab: false,
          hideChartControls: false,
          filters: { visible: true, expanded: false }
        },
      }).then(() => {
        const container = dashboardRef.current;
        if (container && container.children[0]) {
          const iframe = container.children[0];
          // الأبعاد الفعلية (111.11% + zoom 0.9) متحكم فيها من الـ <style> تحت،
          // مش هنا، عشان تفضل !important وتغلب أي inline style بيحطه Superset SDK نفسه.
          iframe.style.border = "none";
        }
      });
    }
  }, [refreshKey]);

  return (
    <div
      ref={containerRef}
      className="bg-surface-card border border-line rounded-card shadow-card mt-2 overflow-hidden flex flex-col transition-all h-full"
    >
      <div className="bg-primary-dark text-white px-3 py-1 flex justify-between items-center text-[11px] font-bold shrink-0" style={{ fontSize: `${fontSize}px` }}>
        <div className={`flex items-center gap-2 `}>
          <BarChart2 size={14} />
          <span>{t('dashboard.advanced_analytics')}</span>
        </div>
        <div className={`flex items-center gap-3 `}>
            <RefreshCw size={12} className={`cursor-pointer hover:rotate-180 transition-transform duration-500 ${isRTL ? 'rotate-180' : ''}`} onClick={handleRefresh} />
            <Maximize2 size={12} className={`cursor-pointer hover:scale-125 transition-transform ${isRTL ? 'rotate-90' : ''}`} onClick={toggleFullScreen} />
        </div>
      </div>

      {/* 🎯 محتوى Superset نفسه بيفضل فاتح دايمًا (dashboardUiConfig.colorScheme='light' تحت)
          — ده قيد من Superset مش حاجة نقدر نلوّنها بالتوكنز، فبنسيبها "جزيرة" فاتحة مقصودة
          بدل ما تبان غلطة في الثيم الغامق.
          dir="ltr" هنا مقصودة: واجهة Superset نفسها مش RTL-aware، ولو سبناها تورّث
          rtl من صفحة التطبيق، الـ scroll الأفقي جوه الفريم بيرسو على الطرف الغلط
          فبتتقص التابات الأولى وأيقونات التحكم (زي ما ظهر لما اللغة عربي).
          🎯 overflow-hidden + items-center كانوا بيقفلوا أي سكرول رأسي خالص - لوحة
          فيها كذا صف شارتات بتبقى أطول من ارتفاع الشاشة، وكانت بتتقص من تحت
          بصمت من غير أي سكرول بار يظهر يخلي اليوزر يوصلها. overflow-y-auto +
          items-start بيسيبوا المحتوى يبدأ من فوق ويتسكرول عادي لأي حتة زيادة،
          مع إبقاء overflow-x-hidden عشان السكرول الأفقي الغير مرغوب فيه (سبب
          الـ zoom trick أصلاً) يفضل مقفول */}
      <div dir="ltr" className="flex-1 w-full bg-surface-panel flex items-start justify-center overflow-y-auto overflow-x-hidden p-2">
        <div ref={dashboardRef} className="w-full h-full bg-white rounded-control shadow-inner" />
      </div>

      {/* 🎯 Superset مصمم لعرض تابت أوسع من المساحة المتاحة، فبيطلع سكرول أفقي جوه
          الفريم نفسه. الحل: نكبّر صندوق الفريم لـ 111.11% (يعني 100/0.9) قبل ما نعمله
          zoom بمقدار 0.9 — كده محتوى Superset بيحس إن عنده مساحة أوسع بـ 11% فيرندر
          من غير سكرول داخلي، وبعدين الـ zoom بيرجع يصغّر الصندوق كله عشان يظبط بالظبط
          عرض الحاوية (111.11% × 0.9 = 100%). ارتفاع اللوحة الفعلي (لما بتبقى أطول
          من الشاشة) بقى بيتسكرول من الحاوية الأب فوق (overflow-y-auto) بدل ما يتقص. */}
      <style>{`
        iframe { background-color: white !important; height: 111.11% !important; width: 111.11% !important; zoom: 0.9; border: none !important; direction: ltr !important; }
        :fullscreen { padding: 0 !important; background: white !important; }
        :fullscreen .shrink-0 { display: flex !important; }
      `}</style>
    </div>
  );
};

// الـ KPI Gauge مترجم
const KpiGauge = ({ title, actual, target, color }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden flex flex-col">
      <div className="bg-primary-dark text-white px-3 py-1 flex justify-between items-center">
        <span className="text-[11px] font-bold">{t('dashboard.kpi_graph')}</span>
        <RefreshCw size={12} />
      </div>
      <div className="p-4 flex flex-col items-center">
        <p className="text-[11px] font-bold text-primary mb-4 text-center h-8 leading-tight">{title}</p>
        <div className="relative w-40 h-24 flex justify-center overflow-hidden">
          <div className="w-40 h-40 rounded-full border-[15px] border-line border-b-transparent rotate-[-45deg]"></div>
          <div
            className="absolute top-0 w-40 h-40 rounded-full border-[15px] border-b-transparent transition-all duration-1000 rotate-[45deg]"
            style={{ borderColor: color, transform: `rotate(${(Math.min(actual / target, 1) * 180) - 45}deg)` }}
          ></div>
          <div className="absolute bottom-2 text-2xl font-black text-ink">{actual}%</div>
        </div>
        <div className="w-full mt-4 flex justify-between text-[11px] border-t border-line pt-2 text-ink">
            <span>{t('dashboard.actual')}: <b className="text-sm">{actual}</b></span>
            <span>{t('dashboard.target')}: <b className="text-sm">{target}</b></span>
        </div>
      </div>
    </div>
  );
};

// --- المكون الرئيسي ---
export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('BI');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  return (
    <div className="h-full flex flex-col bg-surface-app overflow-hidden">
      <div className="bg-primary-dark h-9 flex items-center px-2 text-white text-[11px] shrink-0" style={{ fontSize: `${fontSize}px` }}>
        <button
          onClick={() => setActiveTab('BI')}
          className={`${activeTab === 'BI' ? 'bg-surface-card text-primary font-bold' : 'opacity-70 font-medium'} px-4 py-2 rounded-t-md cursor-pointer transition-all`}
        >
          {t('dashboard.tab_bi')}
        </button>
        <button
          onClick={() => setActiveTab('INBOX')}
          className={`${activeTab === 'INBOX' ? 'bg-surface-card text-primary font-bold' : 'opacity-70 font-medium'} px-4 py-2 rounded-t-md cursor-pointer transition-all ${i18n.language === 'ar' || i18n.language === 'ur' ? 'mr-1' : 'ml-1'} flex items-center gap-2`}
        >
          <Inbox size={12} />
          <span>{t('dashboard.tab_inbox')}</span>
        </button>
      </div>

      <div className="flex-1 min-h-0 p-6 overflow-y-auto overflow-x-hidden">
        {activeTab === 'BI' ? (
          <MaxeamBI />
        ) : (
          <WorkflowInbox />
        )}
      </div>
    </div>
  );
}